import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Pagination,
  Popup,
  Table,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';

import { ITEMS_PER_PAGE } from '../constants';
import { renderQuota } from '../helpers/render';
import { StudioHero, StudioStat } from './StudioPage';

// 统一样式常量（与渠道管理保持一致）—— 深空黑 + 紫蓝霓虹
const styles = {
  page: {
    background: 'transparent',
    borderRadius: 20,
    padding: 20,
  },
  hero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '18px 20px',
    marginBottom: 16,
    borderRadius: 18,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.03) 55%, rgba(0,0,0,0.02) 100%)',
    border: '1px solid rgba(0,0,0,0.12)',
  },
  searchSection: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '14px 16px',
    marginBottom: 16,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
  },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: 12,
    border: '1px solid rgba(0,0,0,0.08)',
    background: '#ffffff',
    boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
  },
  tableHeader: {
    background: '#f5f5f5',
    color: '#18181b',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap',
  },
  tableRow: {
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease',
    background: 'transparent',
  },
  tableRowHover: {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.02) 100%)',
    boxShadow: 'inset 2px 0 0 #18181b, 0 8px 20px rgba(0,0,0,0.06)',
  },
  button: {
    borderRadius: 10,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'transform 0.16s ease, box-shadow 0.16s ease',
  },
};

function renderTimestamp(timestamp) {
  return <>{timestamp2string(timestamp)}</>;
}

/**
 * 渲染令牌状态徽标
 * @param {number} status - 状态码
 * @param {Function} t - 翻译函数
 * @returns {JSX.Element}
 */
function renderStatus(status, t) {
  const cfg = {
    1: { text: t('token.table.status_enabled'), bg: '#f0fdf4', color: '#16a34a', border: 'rgba(22,163,74,0.3)', icon: 'check circle' },
    2: { text: t('token.table.status_disabled'), bg: '#fef2f2', color: '#dc2626', border: 'rgba(220,38,38,0.3)', icon: 'ban' },
    3: { text: t('token.table.status_expired'), bg: '#fffbeb', color: '#d97706', border: 'rgba(217,119,6,0.3)', icon: 'clock' },
    4: { text: t('token.table.status_depleted'), bg: '#f5f5f5', color: '#6b7280', border: 'rgba(0,0,0,0.12)', icon: 'exclamation circle' },
  }[status] || { text: t('token.table.status_unknown'), bg: '#f5f5f5', color: '#6b7280', border: 'rgba(0,0,0,0.12)', icon: 'question circle' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 6,
      background: cfg.bg,
      color: cfg.color,
      fontSize: 12,
      fontWeight: 500,
      border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      <Icon name={cfg.icon} style={{ margin: 0, fontSize: 11 }} /> {cfg.text}
    </span>
  );
}

const TokensTable = () => {
  const { t } = useTranslation();
  const [hoveredRow, setHoveredRow] = useState(null);

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [targetTokenIdx, setTargetTokenIdx] = useState(0);
  const [orderBy, setOrderBy] = useState('');

  const loadTokens = async (startIdx) => {
    const res = await API.get(`/api/token/?p=${startIdx}&order=${orderBy}`);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setTokens(data);
      } else {
        let newTokens = [...tokens];
        newTokens.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
        setTokens(newTokens);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(tokens.length / ITEMS_PER_PAGE) + 1) {
        // In this case we have to load more data and then append them.
        await loadTokens(activePage - 1, orderBy);
      }
      setActivePage(activePage);
    })();
  };

  const refresh = async () => {
    setLoading(true);
    await loadTokens(activePage - 1);
  };

  const onCopy = async (key) => {
    const token = `sk-${key}`;
    if (await copy(token)) {
      showSuccess(t('token.messages.copy_success'));
    } else {
      showWarning(t('token.messages.copy_failed'));
      setSearchKeyword(token);
    }
  };

  useEffect(() => {
    loadTokens(0, orderBy)
      .then()
      .catch((reason) => {
        showError(reason);
      });
  }, [orderBy]);

  const manageToken = async (id, action, idx) => {
    let data = { id };
    let res;
    switch (action) {
      case 'delete':
        res = await API.delete(`/api/token/${id}/`);
        break;
      case 'enable':
        data.status = 1;
        res = await API.put('/api/token/?status_only=true', data);
        break;
      case 'disable':
        data.status = 2;
        res = await API.put('/api/token/?status_only=true', data);
        break;
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('token.messages.operation_success'));
      let token = res.data.data;
      let newTokens = [...tokens];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      if (action === 'delete') {
        newTokens[realIdx].deleted = true;
      } else {
        newTokens[realIdx].status = token.status;
      }
      setTokens(newTokens);
    } else {
      showError(message);
    }
  };

  const searchTokens = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadTokens(0);
      setActivePage(1);
      setOrderBy('');
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/token/search?keyword=${searchKeyword}`);
    const { success, message, data } = res.data;
    if (success) {
      setTokens(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortToken = (key) => {
    if (tokens.length === 0) return;
    setLoading(true);
    let sortedTokens = [...tokens];
    sortedTokens.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedTokens[0].id === tokens[0].id) {
      sortedTokens.reverse();
    }
    setTokens(sortedTokens);
    setLoading(false);
  };

  const handleOrderByChange = (e, { value }) => {
    setOrderBy(value);
    setActivePage(1);
  };

  return (
    <div className='studio-table-page' style={styles.page}>
      <StudioHero
        icon='key'
        kicker='AI STUDIO / ACCESS'
        title={t('token.title')}
        description={t('token.studio.subtitle')}
        className='studio-management-hero'
      />

      <section className='studio-management-overview'>
        <div>
          <span className='studio-section-eyebrow'>ACCESS TOKENS</span>
          <h2>{t('token.studio.overview_title')}</h2>
          <p>{t('token.studio.overview_subtitle')}</p>
        </div>
        <div className='studio-management-overview__stats'>
          <StudioStat label={t('token.studio.total')} value={tokens.length} />
          <StudioStat label={t('token.studio.enabled')} value={tokens.filter((token) => token.status === 1).length} tone='success' />
          <StudioStat label={t('token.studio.disabled')} value={tokens.filter((token) => token.status !== 1).length} tone='danger' />
        </div>
      </section>

      <section className='studio-management-toolbar'>
        <div className='studio-management-toolbar__heading'>
          <div>
            <span className='studio-section-eyebrow'>TOKEN CONTROLS</span>
            <h3>{t('token.studio.search')}</h3>
          </div>
          <Icon name='sliders horizontal' />
        </div>
        <Form onSubmit={searchTokens}>
          <Form.Group className='studio-management-search-row'>
            <Form.Input
              icon='search'
              placeholder={t('token.search')}
              value={searchKeyword}
              loading={searching}
              onChange={handleKeywordChange}
              size='small'
              width={6}
              style={{ borderRadius: 8 }}
            />
            <Form.Button primary size='small' type='submit' style={styles.button}>
              <Icon name='search' /> {t('token.studio.search')}
            </Form.Button>
          </Form.Group>
        </Form>
        <div className='studio-management-toolbar__actions'>
          <Button as={Link} to='/token/add' primary loading={loading} size='mini'>
            <Icon name='plus' /> {t('token.buttons.add')}
          </Button>
          <Button onClick={refresh} loading={loading} size='mini'>
            <Icon name='refresh' /> {t('token.buttons.refresh')}
          </Button>
          <Dropdown
            placeholder={t('token.sort.placeholder')}
            selection
            options={[
              { key: '', text: t('token.sort.default'), value: '' },
              { key: 'remain_quota', text: t('token.sort.by_remain'), value: 'remain_quota' },
              { key: 'used_quota', text: t('token.sort.by_used'), value: 'used_quota' },
            ]}
            value={orderBy}
            onChange={handleOrderByChange}
            style={{ minWidth: 160 }}
          />
        </div>
      </section>

      {/* 令牌表格 */}
      <section className='studio-management-table-card'>
        <div className='studio-management-table-card__head'>
          <div>
            <span className='studio-section-eyebrow'>TOKEN INVENTORY</span>
            <h3>{t('token.title')}</h3>
          </div>
          <span className='studio-record-count'>{tokens.length}</span>
        </div>
        <div className='studio-management-table-scroll'>
        <Table basic='very' compact size='small' className='studio-management-table studio-token-table' style={{ margin: 0, minWidth: 1000 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortToken('name')}>
                {t('token.table.name')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortToken('status')}>
                {t('token.table.status')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortToken('used_quota')}>
                {t('token.table.used_quota')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortToken('remain_quota')}>
                {t('token.table.remain_quota')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortToken('created_time')}>
                {t('token.table.created_time')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortToken('expired_time')}>
                {t('token.table.expired_time')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader}>{t('token.table.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {tokens
              .slice(
                (activePage - 1) * ITEMS_PER_PAGE,
                activePage * ITEMS_PER_PAGE
              )
              .map((token, idx) => {
                if (token.deleted) return <></>;
                const isHovered = hoveredRow === token.id;

                return (
                  <Table.Row
                    key={token.id}
                    style={{
                      ...styles.tableRow,
                      ...(isHovered ? styles.tableRowHover : {}),
                    }}
                    onMouseEnter={() => setHoveredRow(token.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 999,
                          background: '#f5f5f5',
                          color: '#6b7280',
                          fontSize: 12,
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                        }}>#{token.id}</span>
                        <span style={{
                          fontWeight: 800,
                          color: '#18181b',
                          fontSize: 15,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 200,
                        }}>{token.name ? token.name : t('token.table.no_name')}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>{renderStatus(token.status, t)}</Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#18181b', fontVariantNumeric: 'tabular-nums' }}>
                        {renderQuota(token.used_quota, t)}
                      </span>
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>
                        {token.unlimited_quota
                          ? t('token.table.unlimited')
                          : renderQuota(token.remain_quota, t, 2)}
                      </span>
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px', color: '#6b7280', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                      {renderTimestamp(token.created_time)}
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px', color: '#6b7280', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                      {token.expired_time === -1
                        ? t('token.table.never_expire')
                        : renderTimestamp(token.expired_time)}
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Button
                          size='mini'
                          positive
                          onClick={async () => await onCopy(token.key)}
                          style={styles.button}
                        >
                          <Icon name='copy' /> {t('token.buttons.copy')}
                        </Button>
                        <Popup
                          trigger={
                            <Button size='mini' basic negative style={styles.button}>
                              <Icon name='trash' /> {t('token.buttons.delete')}
                            </Button>
                          }
                          on='click'
                          flowing
                          hoverable
                        >
                          <Button
                            size='mini'
                            negative
                            onClick={() => {
                              manageToken(token.id, 'delete', idx);
                            }}
                            style={styles.button}
                          >
                            <Icon name='exclamation triangle' /> {t('token.buttons.confirm_delete')} {token.name}
                          </Button>
                        </Popup>
                        <Button
                          size='mini'
                          basic
                          color={token.status === 1 ? 'red' : 'green'}
                          onClick={() => {
                            manageToken(
                              token.id,
                              token.status === 1 ? 'disable' : 'enable',
                              idx
                            );
                          }}
                          style={styles.button}
                        >
                          <Icon name={token.status === 1 ? 'ban' : 'check'} />
                          {token.status === 1
                            ? t('token.buttons.disable')
                            : t('token.buttons.enable')}
                        </Button>
                        <Button
                          size='mini'
                          as={Link}
                          to={'/token/edit/' + token.id}
                          primary
                          style={styles.button}
                        >
                          <Icon name='edit' /> {t('token.buttons.edit')}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='7'>
                <div className='studio-management-table-footer'>
                  <span>{tokens.length}</span>
                  <Pagination
                    activePage={activePage}
                    onPageChange={onPaginationChange}
                    size='mini'
                    siblingRange={1}
                    totalPages={
                      Math.ceil(tokens.length / ITEMS_PER_PAGE) +
                      (tokens.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                    }
                  />
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
        </div>
      </section>
    </div>
  );
};

export default TokensTable;
